<script setup lang="ts">
import CloseSmallIcon from '@/assets/icons/shared/close-small.svg';
import EditIcon from '@/assets/icons/shared/edit-outline.svg';

defineEmits<{
  edit: [];
  remove: [];
}>();
</script>

<template>
  <div
    class="master-card"
    tabindex="0"
    @click="$emit('edit')"
    @keydown.enter.prevent="$emit('edit')"
    @keydown.space.prevent="$emit('edit')"
  >
    <button
      type="button"
      class="close"
      tabindex="-1"
      @click.stop="$emit('remove')"
    >
      <CloseSmallIcon class="close-icon" />
    </button>

    <div
      class="title"
      title=""
    >
      <slot name="title" />
    </div>

    <div class="body">
      <div class="content">
        <slot />
      </div>

      <button
        type="button"
        class="edit"
        tabindex="-1"
        @click.stop="$emit('edit')"
      >
        <EditIcon class="edit-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.master-card {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 8px;
  flex-direction: column;
  gap: 8px;
  color: var(--color-gray-600);
  background-color: var(--color-gray-075);
  border-radius: 8px;
  cursor: pointer;
}

.title {
  flex-shrink: 0;
  min-width: 0;
  max-width: 100%;
  padding-right: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-l);
  font-weight: 500;
  color: var(--color-gray-900);
}

.body {
  position: relative;
  display: flex;
  margin-top: auto;
  min-width: 0;
  padding: 8px;
  border-radius: 8px;
  background-color: var(--color-gray-100);
}

.content {
  display: flex;
  min-width: 0;
  width: 100%;
  flex-direction: column;
  gap: 8px;

  :slotted(.value) {
    margin: 0;
    min-width: 0;
    color: var(--color-gray-900);
    font-size: var(--font-size-s);
    line-height: var(--line-height-s);
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  > :slotted(*):last-child {
    padding-right: 32px;
  }

  :slotted(._flush) {
    padding-right: 0;
  }
}

.edit,
.close {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: background-color var(--transition-fast);
}

.edit {
  right: 8px;
  bottom: 8px;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: var(--color-gray-500);

  @media (hover: hover) {
    &:hover {
      background-color: var(--color-gray-700);
    }
  }
}

.edit-icon {
  display: block;
  width: 16px;
  height: 16px;
  color: var(--color-white);
}

.close {
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: var(--color-gray-500);

  @media (hover: hover) {
    &:hover {
      background-color: var(--color-gray-700);
    }
  }
}

.close-icon {
  width: 100%;
  height: 100%;
  fill: var(--color-white);
}

@media (hover: hover) {
  .master-card:hover,
  .master-card:focus-visible {
    .close,
    .edit {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .close:focus-visible,
  .edit:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }
}

@media (hover: none) {
  .close,
  .edit {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
